import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { MediaType } from '../types/movie';

interface MediaTypeContextValue {
  mediaType: MediaType;
  setMediaType: (type: MediaType) => void;
  isTV: boolean;
}

const STORAGE_KEY = 'mediaType';

function readMediaTypeFromUrl(): MediaType | null {
  try {
    const type = new URLSearchParams(window.location.search).get('type');
    if (type === 'movie' || type === 'tv') return type;
  } catch {
    // SSR or window unavailable
  }
  return null;
}

function readPersistedMediaType(): MediaType {
  const urlType = readMediaTypeFromUrl();
  if (urlType) return urlType;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'movie' || stored === 'tv') return stored;
  } catch {
    // SSR or localStorage blocked
  }
  return 'movie';
}

const MediaTypeContext = createContext<MediaTypeContextValue | undefined>(undefined);

export function MediaTypeProvider({ children }: { children: ReactNode }) {
  const [mediaType, setMediaTypeRaw] = useState<MediaType>(readPersistedMediaType);

  const setMediaType = useCallback((type: MediaType) => {
    setMediaTypeRaw(type);
    try {
      localStorage.setItem(STORAGE_KEY, type);
    } catch {
      // localStorage blocked
    }
  }, []);

  return (
    <MediaTypeContext.Provider value={{
      mediaType,
      setMediaType,
      isTV: mediaType === 'tv',
    }}>
      {children}
    </MediaTypeContext.Provider>
  );
}

export function useMediaType() {
  const context = useContext(MediaTypeContext);
  if (!context) {
    throw new Error('useMediaType must be used within MediaTypeProvider');
  }
  return context;
}
